import React, { useEffect, useRef, useState } from "react";
import { Link, navigate } from "gatsby";
import { OutboundLink } from "gatsby-plugin-google-gtag";
import { MenuItem, Menu } from "react-aim-menu";
import statenames from "../data/state_abbrev_to_name.json";
// import "../styles/styles.css";
import "../styles/navbar.css";

const NavBar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownItemOpen, setDropdownItemOpen] = useState(null);
  const [selectedState, setSelectedState] = useState("");
  const headerRef = useRef(null);
  const menuButtonRef = useRef(null);

  const closeMobileMenu = () => setMenuOpen(false);

  useEffect(() => {
    if (!menuOpen) return;

    const handleKeyDown = event => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        if (menuButtonRef.current) menuButtonRef.current.focus();
      }
    };
    const handleOutsideClick = event => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    const handleResize = () => {
      if (window.innerWidth >= 768) setMenuOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("click", handleOutsideClick);
    window.addEventListener("resize", handleResize);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("click", handleOutsideClick);
      window.removeEventListener("resize", handleResize);
    };
  }, [menuOpen]);

  return (
    <div className="nav-bar-container">
      <div className="breaking-update">
        <span>
          For the latest on gerrymandering and other problems, read PGP Director Sam Wang’s{' '}
          <Link to="https://samwang.substack.com">Fixing Bugs In Democracy</Link>.
        </span>
      </div>
      <div className="header-bar" ref={headerRef}>
        <Link className="nav-logo" to="/">
          Gerrymandering Project
        </Link>
        <button
          type="button"
          ref={menuButtonRef}
          onClick={() => setMenuOpen(open => !open)}
          className="nav-menu-button"
          aria-expanded={menuOpen}
          aria-controls="mobile-navigation"
          aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
        >
          <span aria-hidden="true" className="mobile-menu-icon">{menuOpen ? "×" : "☰"}</span>
          <span>{menuOpen ? "Close" : "Menu"}</span>
        </button>
        {menuOpen && (
          <nav id="mobile-navigation" className="nav-menu-dropdown mobile-navigation" aria-label="Mobile navigation">
            <Link className="mobile-nav-primary" to="/" onClick={closeMobileMenu}>Home</Link>
            <Link className="mobile-nav-primary" to="/redistricting-report-card" onClick={closeMobileMenu}>Redistricting Report Card</Link>

            <details className="mobile-nav-section" name="mobile-nav-sections">
              <summary>About</summary>
              <ul>
                <li><Link to="/about" onClick={closeMobileMenu}>About the Project</Link></li>
                <li><Link to="/redistricting-report-card-methodology" onClick={closeMobileMenu}>Methodology</Link></li>
                <li><Link to="/team" onClick={closeMobileMenu}>Team</Link></li>
              </ul>
            </details>

            <details className="mobile-nav-section" name="mobile-nav-sections">
              <summary>Resources</summary>
              <ul>
                <li><Link to="/press" onClick={closeMobileMenu}>Press</Link></li>
                <li><Link to="/scholarly-work" onClick={closeMobileMenu}>Scholarly Work</Link></li>
                <li><Link to="/data-code" onClick={closeMobileMenu}>Data &amp; Code</Link></li>
                <li><Link to="/useful-links" onClick={closeMobileMenu}>Useful Links</Link></li>
              </ul>
            </details>

            <details className="mobile-nav-section" name="mobile-nav-sections">
              <summary>States</summary>
              <div className="mobile-nav-state-picker">
                <label htmlFor="mobile-nav-state">Choose a state</label>
                <div className="mobile-nav-state-controls">
                  <select id="mobile-nav-state" value={selectedState} onChange={event => setSelectedState(event.target.value)}>
                    <option value="" disabled>Select a state</option>
                    {Object.keys(statenames).map(state => <option key={state} value={state}>{statenames[state]}</option>)}
                  </select>
                  <button type="button" disabled={!selectedState} onClick={() => {
                    if (!selectedState) return;
                    closeMobileMenu();
                    navigate(`/reforms/${selectedState}`);
                  }}>Go <span aria-hidden="true">→</span></button>
                </div>
              </div>
            </details>

            <details className="mobile-nav-section" name="mobile-nav-sections">
              <summary>Related Projects</summary>
              <ul>
                <li><OutboundLink target="_blank" rel="noopener noreferrer" href="https://electoral-lab.org" onClick={closeMobileMenu}>Electoral Lab</OutboundLink></li>
                <li><OutboundLink target="_blank" rel="noopener noreferrer" href="https://election.princeton.edu" onClick={closeMobileMenu}>PEC</OutboundLink></li>
                <li><OutboundLink target="_blank" rel="noopener noreferrer" href="https://representable.org" onClick={closeMobileMenu}>Representable</OutboundLink></li>
              </ul>
            </details>

            <OutboundLink className="mobile-nav-primary mobile-nav-feedback" target="_blank" rel="noopener noreferrer" href="https://docs.google.com/forms/d/e/1FAIpQLScx9z7g6Kd7wkTYNtsD59XmxFN6ZdRpVa2bQx2UR6XrZiQmJA/viewform?usp=sf_link" onClick={closeMobileMenu}>Submit Feedback <span aria-hidden="true">↗</span></OutboundLink>
          </nav>
        )}
        <ul className="nav-buttons">
          <Menu onMouseLeave={() => setDropdownItemOpen(null)}>
            <MenuItem onHover={() => setDropdownItemOpen("About")}>
              <a>About {dropdownItemOpen === "About" ? "▲" : "▼"}</a>

              <ul
                className="nav-menu-dropdown-item"
                style={{
                  display: dropdownItemOpen === "About" ? "block" : "none",
                }}
              >
                <li>
                  <Link to="/about">About</Link>
                </li>
                <li>
                  <Link to="/redistricting-report-card-methodology">
                    Methodology
                  </Link>
                </li>
                <li>{<Link to="/team">Team</Link>}</li>
              </ul>
            </MenuItem>
          </Menu>
          {/* <Menu onMouseLeave={() => setDropdownItemOpen(null)}>
            <MenuItem onHover={() => setDropdownItemOpen("Maps")}>
              <a>Maps {dropdownItemOpen === "Maps" ? "▲" : "▼"}</a>

              <ul
                className="nav-menu-dropdown-item"
                style={{
                  display: dropdownItemOpen === "Maps" ? "block" : "none",
                }}
              >
                <li>
                  <Link to="/redistricting-report-card">
                    Redistricting Report Card
                  </Link>
                </li>
                <li>
                  <Link to="/redistricting-timeline-alert-map">
                    Redistricting Timeline Alert
                  </Link>
                </li>
                <li>
                  <Link to="/reforms">Reforms</Link>
                </li>
                <li>
                  <Link to="/tests">Gerrymandering tests (old)</Link>
                </li>
              </ul>
            </MenuItem>
          </Menu> */}
          <li>
            <Link to="/redistricting-report-card">Map</Link>
          </li>
          <Menu onMouseLeave={() => setDropdownItemOpen(null)}>
            <MenuItem onHover={() => setDropdownItemOpen("Resources")}>
              <a>Resources {dropdownItemOpen === "Resources" ? "▲" : "▼"}</a>

              <ul
                className="nav-menu-dropdown-item"
                style={{
                  display: dropdownItemOpen === "Resources" ? "block" : "none",
                }}
              >
                <li>
                  <Link to="/press">Press</Link>
                </li>
                <li>
                  <Link to="/scholarly-work">Scholarly Work</Link>
                </li>
                <li>
                  <Link to="/data-code">Data & Code</Link>
                </li>
                <li>
                  <Link to="/useful-links">Useful Links</Link>
                </li>
              </ul>
            </MenuItem>
          </Menu>
          {/* <li>
            <Link to="/resources">Resources</Link>
          </li> */}
          <Menu onMouseLeave={() => setDropdownItemOpen(null)}>
            <MenuItem onHover={() => setDropdownItemOpen("States")}>
              <a>States {dropdownItemOpen === "States" ? "▲" : "▼"}</a>

              <ul
                className="nav-menu-dropdown-item"
                style={{
                  display: dropdownItemOpen === "States" ? "block" : "none",
                }}
              >
                {Object.keys(statenames).map(statename => {
                  return (
                    <li key={statename} value={statename}>
                      <Link to={`/reforms/${statename}`}>
                        {statenames[statename]}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </MenuItem>
          </Menu>

          <li>|</li>
          <li>
            <OutboundLink
              target="_blank"
              rel="noopener noreferrer"
              href="https://docs.google.com/forms/d/e/1FAIpQLScx9z7g6Kd7wkTYNtsD59XmxFN6ZdRpVa2bQx2UR6XrZiQmJA/viewform?usp=sf_link"
            >
              Submit Feedback
            </OutboundLink>
          </li>
          <Menu onMouseLeave={() => setDropdownItemOpen(null)}>
            <MenuItem onHover={() => setDropdownItemOpen("Related Projects")}>
              <a>
                Related Projects{" "}
                {dropdownItemOpen === "Related Projects" ? "▲" : "▼"}
              </a>

              <ul
                className="nav-menu-dropdown-item"
                style={{
                  display:
                    dropdownItemOpen === "Related Projects" ? "block" : "none",
                }}
              >
                <li>
                  <OutboundLink
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://electoral-lab.org"
                  >
                    Electoral Lab
                  </OutboundLink>
                </li>
                <li>
                  <OutboundLink
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://election.princeton.edu"
                  >
                    PEC
                  </OutboundLink>
                </li>
                <li>
                  <OutboundLink
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://representable.org"
                  >
                    Representable
                  </OutboundLink>
                </li>
              </ul>
            </MenuItem>
          </Menu>
        </ul>
        {/* <div className="header-bar-search">Search</div> */}
      </div>
    </div>
  );
};

export default NavBar;
